import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export const NewProjectDialog = ({ mode, cfg, cat, open, onOpenChange, initialCommodity }) => {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", commodities: [], country: "", client_name: "" });
  const [clients, setClients] = useState([]);
  const [clientMode, setClientMode] = useState("existing");

  useEffect(() => {
    if (open) {
      api.get("/clients").then((r) => { setClients(r.data.clients || []); setClientMode((r.data.clients || []).length ? "existing" : "new"); }).catch(() => {});
      if (initialCommodity) setForm((f) => ({ ...f, commodities: f.commodities.includes(initialCommodity) ? f.commodities : [...f.commodities, initialCommodity] }));
    }
  }, [open, initialCommodity]);

  const create = async () => {
    if (!form.name) return toast.error("Project name is required");
    try {
      const payload = { name: form.name, country: form.country, client_name: form.client_name, mode };
      if (cfg.commodity && form.commodities.length) payload.commodities = form.commodities;
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      onOpenChange(false);
      nav(cfg.open(data.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="new-project-btn"><Plus size={16} className="mr-1" />New Project</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Create {cfg.overline} Project</DialogTitle>
          <DialogDescription className="sr-only">Enter the project name, commodity and client to create a new {cfg.overline} project.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Project Name *</Label>
            <Input data-testid="np-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            {cfg.commodity && (
              <div className="col-span-2"><Label>Target Minerals <span className="text-muted-foreground font-normal">(select one or more)</span></Label>
                {form.commodities.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="np-commodity-chips">
                    {form.commodities.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 text-xs font-semibold bg-primary/10 text-primary border border-primary/30 rounded-full pl-2 pr-1 py-0.5">
                        {c}<button type="button" data-testid={`np-commodity-rm-${c}`} onClick={() => setForm({ ...form, commodities: form.commodities.filter((x) => x !== c) })} className="hover:text-red-600"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <Select value="" onValueChange={(v) => { if (v && !form.commodities.includes(v)) setForm({ ...form, commodities: [...form.commodities, v] }); }}>
                  <SelectTrigger data-testid="np-commodity" className="mt-1.5"><SelectValue placeholder="+ Add mineral…" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {(cat?.commodities || []).filter((c) => !form.commodities.includes(c)).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={cfg.commodity ? "col-span-2" : ""}><Label>Country</Label>
              <Input data-testid="np-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label>Client / Company Folder</Label>
            {clientMode === "existing" && clients.length > 0 ? (
              <Select value={form.client_name || undefined}
                onValueChange={(v) => (v === "__new__" ? (setClientMode("new"), setForm({ ...form, client_name: "" })) : setForm({ ...form, client_name: v }))}>
                <SelectTrigger className="mt-1" data-testid="np-client-select"><SelectValue placeholder="Select an existing client…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value="__new__">+ Add new client…</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2 mt-1">
                <Input data-testid="np-client" placeholder="New client / company name" value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })} autoFocus />
                {clients.length > 0 && (
                  <Button type="button" variant="ghost" size="sm" className="shrink-0 text-xs" data-testid="np-client-existing"
                    onClick={() => { setClientMode("existing"); setForm({ ...form, client_name: "" }); }}>Pick existing</Button>
                )}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">New names are saved to your Clients list and the project is grouped into that folder automatically.</p>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="np-create" onClick={create}>Create & Open</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
